import Vue from 'vue'
import Toasted from 'vue-toasted'
import textUtil from './textUtil'

Vue.use(Toasted)
const commonProps = {
  viewBox: '0 0 22 22',
  width: '22',
  height: '22',
  maintainColor: false,
  class: 'mt-1.5', // Custom class for spacing/alignment
}
// Define a mapping for icon components based on class names
const ICON_COMPONENT_MAP = {
  // Key is a fragment of the className, value is an object containing the component and its props
  info: {
    // We assume 'AhaIcon' is either globally registered or imported and accessible here.
    // If AhaIcon needs to be imported, you would do:
    // component: AhaIcon,
    // For simplicity, let's assume AhaIcon is registered globally or accessible.
    // If not, you'd need to pass the actual imported component:
    // component: require('./path/to/AhaIcon.vue').default, // or import AhaIcon above
    name: 'info-circle-o', // This will be passed as the 'name' prop to AhaIcon
    props: commonProps,
  },
  success: {
    name: 'check-circle-o', // Example name for success icon
    props: commonProps,
  },
  error: {
    name: 'exclamation-circle-o', // Example name for error icon
    props: commonProps,
  },
  warning: {
    name: 'exclamation-triangle-o', // Example name for warning icon
    props: commonProps,
  },
  // Add more mappings as needed
}

export default {
  showToast(text, options, customOptions) {
    if (!text) return

    const sanitizedText = textUtil.sanitize(text, false, false)
    let toastMessage = sanitizedText
    let iconToRender = null
    let iconPropsToRender = {}
    const customClass = customOptions?.className ?? customOptions?.class ?? 'custom-toast__info'
    // 1. Try to infer icon from className
    if (customClass) {
      const iconKey = Object.keys(ICON_COMPONENT_MAP).find((key) => customClass.includes(key))
      if (iconKey) {
        // Assuming `AhaIcon` is the base component that takes a `name` prop
        // You MUST ensure `AhaIcon` is globally available or imported as a component.
        iconToRender = Vue.component('AhaIcon') // Get globally registered AhaIcon component
        if (!iconToRender) {
          console.warn("AhaIcon component not found. Please ensure it's globally registered or imported.")
          // Fallback or skip icon rendering if component isn't available
          iconToRender = null
        }

        iconPropsToRender = {
          name: ICON_COMPONENT_MAP[iconKey].name,
          ...ICON_COMPONENT_MAP[iconKey].props,
        }
      }
    }

    // 2. Allow customOptions.iconComponent to override inferred icon
    if (customOptions && customOptions.iconComponent) {
      iconToRender = customOptions.iconComponent
      iconPropsToRender = customOptions.iconComponentProps || {}
    }

    // If an icon component is determined (either inferred or explicitly passed)
    if (iconToRender) {
      // Create a temporary Vue instance to render the icon component
      const IconInstance = new Vue({
        render: (h) => h(iconToRender, { props: iconPropsToRender }),
      }).$mount()

      // Get the rendered HTML of the icon component
      const iconHtml = IconInstance.$el.outerHTML

      // Prepend the icon HTML to the toast message.
      toastMessage = `<span class="custom-toast__icon">${iconHtml}</span> ${sanitizedText}`

      // Destroy the temporary instance to prevent memory leaks
      IconInstance.$destroy()
    }

    const currentToast = Vue.toasted.show(toastMessage, {
      theme: 'bubble',
      position: 'bottom-right',
      duration: (customOptions && customOptions.duration) || null,
      className: customClass,
      action: (customOptions && customOptions.action) || {
        text: '',
        onClick: (e, toastObject) => {
          toastObject.goAway(0)
        },
      },
      htmlAllowed: true,
      ...options,
    })
    if (customOptions && customOptions.hasReturnValue) {
      // eslint-disable-next-line consistent-return
      return currentToast
    }
  },
}
